import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/classnames'
import { Loader2 } from 'lucide-react'

const buttonVariants = cva(
  'inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline: 'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        defaultGhost:
          'bg-accent hover:bg-accent/80 border border-background hover:border-accent text-accent-foreground!',
        defaultOutline: 'bg-accent/90 hover:bg-accent text-accent-foreground! border',
      },
      size: {
        default: 'h-9 px-2 sm:px-4 py-1',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-auto rounded-md py-[14px] px-[16px]',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, asChild = false, leftIcon, rightIcon, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} disabled={loading} {...props}>
        {!!loading ? (
          <div className="flex gap-[5px] items-center">
            {leftIcon}
            {typeof props.children === typeof '' &&
              props.children?.toString().slice(0, props.children?.toLocaleString().length - 3) + '..'}
            <Loader2 className="h-4 w-4 animate-spin" />
            {rightIcon}
          </div>
        ) : (
          <>
            {leftIcon}
            {props.children}
            {rightIcon}
          </>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
